import React, { useState, useEffect } from 'react'

import slide1 from '../../../assets/auth/carousel/Slide-1.webp'
import slide2 from '../../../assets/auth/carousel/Slide-2.webp'
import slide3 from '../../../assets/auth/carousel/Slide-3.webp'

const SLIDES = [
  {
    image: slide1,
    badge: 'CONNECTED CAMPUS',
    title: 'Connecting every part of your school, seamlessly.',
    subtitle:
      'Bring academics, administration, communication, and people together through one connected platform.',
  },
  {
    image: slide2,
    badge: 'SMART MANAGEMENT',
    title: 'Empowering institutions with modern tools.',
    subtitle:
      'Real-time notifications, attendance management, and streamlined workflows.',
  },
  {
    image: slide3,
    badge: 'DATA-DRIVEN INSIGHTS',
    title: 'Transform your administrative decisions.',
    subtitle:
      'Comprehensive analytics, automated grading, and integrated reporting systems.',
  },
]

interface AuthCarouselProps {
  children?: React.ReactNode
}

export const AuthCarousel: React.FC<AuthCarouselProps> = ({ children }) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  useEffect(() => {
    if (isPaused) return
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % SLIDES.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [isPaused])

  const currentSlide = SLIDES[currentIndex]

  return (
    <div
      className="relative w-full h-full min-h-screen overflow-hidden select-none bg-slate-900"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background Images Crossfade */}
      {SLIDES.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            index === currentIndex ? 'opacity-100 z-0' : 'opacity-0 -z-10'
          }`}
        >
          <img
            src={slide.image}
            alt="Campus visual"
            className="w-full h-full object-cover object-center"
          />
        </div>
      ))}

      {/* Top Left Logo Slot */}
      {children}

      {/* Bottom Gradient Scrim & Content - WIDE CONTAINER */}
      <div className="absolute inset-x-0 bottom-0 z-20 pointer-events-none bg-gradient-to-t from-black/95 via-black/60 to-transparent pt-40 pb-12 px-8 lg:px-14 flex flex-col justify-end">
        {/* Pill Badge */}
        <div className="inline-flex items-center px-3.5 py-1 rounded-full border border-white/20 bg-black/40 backdrop-blur-md mb-4 self-start pointer-events-auto">
          <span className="text-[11px] font-semibold tracking-wider text-white uppercase">
            {currentSlide.badge}
          </span>
        </div>

        {/* Headline: STRICT block display, full width up to max-w-xl */}
        <h2 className="text-white text-3xl lg:text-[38px] font-bold leading-[1.2] tracking-tight mb-3 max-w-[560px] w-full block whitespace-normal">
          {currentSlide.title}
        </h2>

        {/* Subtitle */}
        <p className="text-white/80 text-sm lg:text-base font-normal leading-relaxed max-w-[500px] w-full block mb-8 whitespace-normal">
          {currentSlide.subtitle}
        </p>

        {/* 3 Horizontal Progress Bars */}
        <div className="w-full max-w-[320px] flex items-center gap-2 pointer-events-auto">
          {SLIDES.map((_, idx) => (
            <div
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className="relative h-1.5 flex-1 bg-white/30 rounded-full overflow-hidden cursor-pointer"
            >
              {idx === currentIndex && (
                <div
                  key={currentIndex}
                  className="absolute inset-y-0 left-0 bg-[#00e5cc] rounded-full animate-[progress_5s_linear]"
                  style={{
                    animation: isPaused ? 'none' : 'progress 5s linear forwards',
                    width: isPaused ? '50%' : undefined,
                  }}
                />
              )}
              {idx < currentIndex && (
                <div className="absolute inset-0 bg-[#00e5cc] rounded-full" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
